import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatTL } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Search, Loader2, Package } from "lucide-react";

const STATUS_MAP = {
  payment_pending: { label: "Ödeme Bekliyor", color: "bg-yellow-500/15 text-yellow-400", step: 0 },
  payment_success: { label: "Ödeme Başarılı", color: "bg-blue-500/15 text-blue-400", step: 1 },
  order_created: { label: "Sipariş Oluşturuldu", color: "bg-blue-500/15 text-blue-400", step: 2 },
  processing: { label: "İşlemde", color: "bg-indigo-500/15 text-indigo-400", step: 3 },
  completed: { label: "Tamamlandı", color: "bg-green-500/15 text-green-400", step: 4 },
  partial: { label: "Kısmen Tamamlandı", color: "bg-orange-500/15 text-orange-400", step: 4 },
  cancelled: { label: "İptal", color: "bg-red-500/15 text-red-400", step: 0 },
  error: { label: "Hata", color: "bg-red-500/15 text-red-400", step: 0 },
};
const FLOW = ["Ödeme", "Onay", "Oluşturuldu", "İşlemde", "Tamamlandı"];

export default function Track() {
  const [params] = useSearchParams();
  const [orderNo, setOrderNo] = useState(params.get("order") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (no) => {
    const q = (no ?? orderNo).trim();
    if (!q) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/orders/track?order_number=${encodeURIComponent(q)}`);
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("order")) search(params.get("order")); }, []); // eslint-disable-line

  const st = order ? STATUS_MAP[order.status] || STATUS_MAP.processing : null;

  return (
    <SiteLayout>
      <Seo title="Sipariş Takip - TakipciSatis.com" canonical="https://takipcisatis.com/siparis-takip" />
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white">Sipariş Takip</h1>
          <p className="text-gray-400 mt-2">Sipariş numaranızı girerek durumunu öğrenin.</p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-2 flex-1 bg-[#171922] border border-white/10 rounded-xl px-4">
            <Search size={18} className="text-gray-400" />
            <input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Sipariş numaranız (TS...)" className="flex-1 bg-transparent py-3.5 text-white outline-none placeholder:text-gray-500" data-testid="track-input" />
          </div>
          <button onClick={() => search()} className="btn-gradient px-6 rounded-xl font-bold" data-testid="track-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Sorgula"}
          </button>
        </div>

        {error && <div className="card-surface mt-6 p-6 text-center text-red-400" data-testid="track-error">{error}</div>}

        {order && st && (
          <div className="card-surface mt-6 p-6" data-testid="track-result">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
              <div className="flex items-center gap-2">
                <Package size={20} className="text-[#d8b4fe]" />
                <span className="font-bold text-white">{order.order_number}</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${st.color}`} data-testid="track-status">{st.label}</span>
            </div>
            {st.step > 0 && (
              <div className="flex items-center mb-6">
                {FLOW.map((f, i) => (
                  <div key={f} className="flex items-center flex-1 last:flex-none">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i < st.step ? "bg-green-500 text-white" : i === st.step ? "bg-white text-[#1f222d]" : "bg-white/10 text-gray-500"}`}>{i + 1}</div>
                      <span className="text-[10px] mt-1 text-gray-400 hidden sm:block">{f}</span>
                    </div>
                    {i < FLOW.length - 1 && <div className={`h-px flex-1 mx-1 ${i < st.step ? "bg-green-500" : "bg-white/10"}`} />}
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              {[
                ["Servis", order.service_name], ["Platform", order.platform_label],
                ["Hedef", order.link], ["Miktar", order.quantity], ["Tutar", formatTL(order.total)],
                ["Oluşturulma", (order.created_at || "").replace("T", " ").slice(0, 16)],
                ["Son Güncelleme", (order.updated_at || "").replace("T", " ").slice(0, 16)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 text-sm border-b border-white/5 pb-2">
                  <span className="text-gray-400 shrink-0">{k}</span>
                  <span className="text-white text-right break-all">{v}</span>
                </div>
              ))}
              {order.provider_status?.remains != null && (
                <div className="flex justify-between text-sm pt-1"><span className="text-gray-400">Kalan</span><span className="text-white">{order.provider_status.remains}</span></div>
              )}
            </div>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}
